import {ComparativeAnalysisPage, PlantContextProvider, PomNavigationProvider} from '@features/pom'
import type {PomNavigationConfig} from '@features/pom'
import {InvertedSurfaceProvider} from '@hconnect/common/styles'
import {Loader} from '@hconnect/uikit/src/lib2'
import {UpmPlantsContextValue, useUpmPlantsContext} from '@hconnect/upm'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {Route, Routes, Outlet, generatePath} from 'react-router'

import {
  DASHBOARD,
  ROOT,
  PLANT_SECTION,
  PLANT_SUB_SECTION,
  ACTIONS,
  REF_LINKS,
  COMPARATIVE_ANALYSIS,
  ERROR_403
} from './common/routing'
import {DefaultShell} from './containers/DefaultShell'
import {ActionsPage} from './pages/ActionsPage'
import {DashboardPage} from './pages/DashboardPage'
import {ErrorPage} from './pages/ErrorPage'
import {PlantSectionPage} from './pages/PlantSectionPage'
import {PlantsGlobalOverviewPage} from './pages/PlantsGlobalOverviewPage'
import {PlantSubSectionPage} from './pages/PlantSubSectionPage'
import {PomError403Page} from './pages/PomError403Page'
import {ReferenceLinksPage} from './pages/ReferenceLinksPage'
import {PomPlantIdProvider} from './providers/PomPlantIdProvider'

/**
 * Resolves a upmId to a plantCode using the UPM plants map.
 * Features/pom always navigates using upmId; the POM entrypoint
 * maps it back to plantCode for its URL structure (/:plantId/...).
 */
const resolveToPlantCode = (upmPlants: UpmPlantsContextValue, upmId: string): string => {
  const entry = Object.entries(upmPlants).find(([, plant]) => plant.upmId === upmId)
  return entry ? entry[0] : upmId
}

const buildPomNavigationConfig = (upmPlants: UpmPlantsContextValue): PomNavigationConfig => ({
  buildOverviewPath: () => ROOT,
  buildDashboardPath: (upmId: string) =>
    generatePath(DASHBOARD, {plantId: resolveToPlantCode(upmPlants, upmId)}),
  buildSectionPath: (upmId: string, sectionId: string) =>
    generatePath(PLANT_SECTION, {plantId: resolveToPlantCode(upmPlants, upmId), sectionId}),
  buildSubSectionPath: (upmId: string, sectionId: string, topicId: string) =>
    generatePath(PLANT_SUB_SECTION, {
      plantId: resolveToPlantCode(upmPlants, upmId),
      sectionId,
      topicId
    })
})

const PomPlantProviders: React.FC = () => (
  <PomPlantIdProvider>
    <PlantContextProvider>
      <Outlet />
    </PlantContextProvider>
  </PomPlantIdProvider>
)

export const Main: React.FC = () => {
  const {t} = useTranslation()
  const upmPlants = useUpmPlantsContext({isInitialLoad: true})
  const pomNavigationConfig = useMemo(
    () => (upmPlants ? buildPomNavigationConfig(upmPlants) : undefined),
    [upmPlants]
  )

  if (!upmPlants || !pomNavigationConfig) {
    return <Loader height="60vh" color="primary.contrastText" />
  }

  return (
    <DefaultShell>
      <PomNavigationProvider config={pomNavigationConfig}>
        <Routes>
          <Route path={ROOT} element={<PlantsGlobalOverviewPage />} />

          <Route path={ERROR_403} element={<PomError403Page />} />

          <Route element={<PomPlantProviders />}>
            <Route path={DASHBOARD} element={<DashboardPage />} />
            <Route path={PLANT_SECTION} element={<PlantSectionPage />} />
            <Route path={PLANT_SUB_SECTION} element={<PlantSubSectionPage />} />
            <Route path={ACTIONS} element={<ActionsPage />} />
            <Route path={REF_LINKS} element={<ReferenceLinksPage />} />
            <Route
              path={COMPARATIVE_ANALYSIS}
              element={
                <InvertedSurfaceProvider>
                  <ComparativeAnalysisPage />
                </InvertedSurfaceProvider>
              }
            />
          </Route>

          <Route element={<ErrorPage errorMessage={t('errors.pageNotFound')} />} />
        </Routes>
      </PomNavigationProvider>
    </DefaultShell>
  )
}
